import { useEffect, useState } from 'react'
import { adminApi } from '@/services/adminApi'
import type { EmailTemplate, TemplateCategory } from '@/types/communications'
import {
    Badge,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    ConfirmDialog,
    EmptyState,
    Field,
    Input,
    LoadingPanel,
    PillButton,
    SectionTitle,
    Toggle,
} from './_shared'
import { RichTextEditor } from '../../components/editor/RichTextEditor'
import { AIGenerateTemplateDrawer } from './AIGenerateTemplateDrawer'
import { PreviewPane } from './PreviewPane'
import { SlotValueForm } from './SlotValueForm'
import type { SlotDefinition } from '@/types/communications'
import {
    FileText,
    Plus,
    Trash2,
    Send,
    Eye,
    Code,
    ChevronLeft,
    Sparkles,
} from 'lucide-react'

const USER_VARIABLES = [
    { key: 'user.firstName', label: 'First name' },
    { key: 'user.name', label: 'Full name' },
    { key: 'user.email', label: 'Email' },
    { key: 'user.orgName', label: 'Organization name' },
]
const DIGEST_VARIABLES = [
    { key: 'digest.chatCount', label: 'Chat messages (count)' },
    { key: 'digest.meetingsCount', label: 'Meetings (count)' },
    { key: 'digest.workflowsOk', label: 'Workflows succeeded' },
    { key: 'digest.workflowsFailed', label: 'Workflows failed' },
    { key: 'digest.arcReports', label: 'ARC reports (count)' },
    { key: 'digest.appsCount', label: 'Libra Apps (count)' },
]
// Variables surfaced in the "Insert variable" menu of the body editor. Using
// the `user.` / `digest.` prefixed forms here is intentional: the renderer
// auto-binds those per-recipient at send time. Bare forms (`{{firstName}}`)
// are treated as static admin-filled values and will not personalize.
const TEMPLATE_VARIABLES = USER_VARIABLES

export function TemplatesTab() {
    const [items, setItems] = useState<EmailTemplate[]>([])
    const [loading, setLoading] = useState(true)
    const [active, setActive] = useState<EmailTemplate | null>(null)
    const [creating, setCreating] = useState(false)
    const [category, setCategory] = useState<TemplateCategory | ''>('')
    const [aiOpen, setAiOpen] = useState(false)

    const load = async () => {
        setLoading(true)
        try {
            const res = await adminApi.getEmailTemplates(
                category ? { category } : undefined,
            )
            setItems(res.items)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category])

    if (creating) {
        return (
            <TemplateEditor
                onCancel={() => setCreating(false)}
                onSaved={async saved => {
                    await load()
                    setCreating(false)
                    setActive(saved)
                }}
            />
        )
    }

    if (active) {
        return (
            <TemplateEditor
                template={active}
                onCancel={() => setActive(null)}
                onDeleted={async () => {
                    await load()
                    setActive(null)
                }}
                onSaved={async saved => {
                    await load()
                    setActive(saved)
                }}
            />
        )
    }

    return (
        <div className="space-y-6">
            <SectionTitle
                title="Templates"
                description="Reusable email payloads with versioning. Each save creates a new version of the same key."
                actions={
                    <div className="flex items-center gap-2">
                        <select
                            value={category}
                            onChange={e =>
                                setCategory(e.target.value as TemplateCategory | '')
                            }
                            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm"
                        >
                            <option value="">All categories</option>
                            <option value="BROADCAST">Broadcast</option>
                            <option value="CAMPAIGN">Campaign</option>
                            <option value="DIGEST_BLOCK">Digest block</option>
                        </select>
                        <PillButton onClick={() => setAiOpen(true)}>
                            <Sparkles className="h-3.5 w-3.5" /> Generate with AI
                        </PillButton>
                        <PillButton variant="primary" onClick={() => setCreating(true)}>
                            <Plus className="h-3.5 w-3.5" /> New template
                        </PillButton>
                    </div>
                }
            />

            {aiOpen && (
                <AIGenerateTemplateDrawer
                    onClose={() => setAiOpen(false)}
                    onSaved={async saved => {
                        setAiOpen(false)
                        await load()
                        setActive(saved)
                    }}
                />
            )}

            {loading ? (
                <LoadingPanel label="Loading templates…" />
            ) : items.length === 0 ? (
                <EmptyState
                    icon={FileText}
                    title="No templates yet"
                    description="Create reusable email templates here. Each save creates a new version of the same key."
                    action={
                        <PillButton variant="primary" onClick={() => setCreating(true)}>
                            <Plus className="h-3.5 w-3.5" /> Create your first template
                        </PillButton>
                    }
                />
            ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {items.map(t => (
                        <button
                            key={t.id}
                            onClick={() => setActive(t)}
                            className="group rounded-lg border border-gray-200 bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-blue-400 hover:shadow-md"
                        >
                            <div className="mb-2 flex items-start justify-between gap-2">
                                <code className="truncate font-mono text-xs text-blue-600">
                                    {t.key}
                                </code>
                                <Badge variant="default">v{t.version}</Badge>
                            </div>
                            {t.name && (
                                <div className="mb-1 text-sm font-semibold text-gray-900">
                                    {t.name}
                                </div>
                            )}
                            <div className="mb-3 line-clamp-2 text-sm text-gray-700 group-hover:text-blue-700">
                                {t.subject}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                {t.category && (
                                    <Badge variant="default">{t.category}</Badge>
                                )}
                                {t.source === 'AI_GENERATED' && (
                                    <Badge variant="default">AI</Badge>
                                )}
                                {t.source === 'SEEDED' && (
                                    <Badge variant="default">seeded</Badge>
                                )}
                                <span>{new Date(t.updatedAt).toLocaleDateString()}</span>
                                <Badge variant={t.active ? 'success' : 'default'}>
                                    {t.active ? 'active' : 'inactive'}
                                </Badge>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}

function TemplateEditor({
    template,
    onCancel,
    onSaved,
    onDeleted,
}: {
    template?: EmailTemplate
    onCancel: () => void
    onSaved: (t: EmailTemplate) => void | Promise<void>
    onDeleted?: () => void | Promise<void>
}) {
    const isNew = !template
    const [key, setKey] = useState(template?.key ?? '')
    const [subject, setSubject] = useState(template?.subject ?? '')
    const [bodyHtml, setBodyHtml] = useState(template?.bodyHtml ?? '')
    const [bodyText, setBodyText] = useState(template?.bodyText ?? '')
    const [active, setActive] = useState(template?.active ?? true)
    const [view, setView] = useState<'editor' | 'preview' | 'html'>('editor')
    const [busy, setBusy] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [testTo, setTestTo] = useState('')
    const [testResult, setTestResult] = useState<string | null>(null)

    const handleSave = async () => {
        setBusy(true)
        try {
            if (isNew) {
                const created = await adminApi.createEmailTemplate({
                    key,
                    subject,
                    bodyHtml,
                    bodyText: bodyText || undefined,
                    active,
                })
                await onSaved(created)
            } else {
                const updated = await adminApi.updateEmailTemplate(template!.id, {
                    subject,
                    bodyHtml,
                    bodyText,
                    active,
                })
                await onSaved(updated)
            }
        } finally {
            setBusy(false)
        }
    }

    const handleDelete = async () => {
        if (!template || !onDeleted) return
        setBusy(true)
        try {
            await adminApi.deleteEmailTemplate(template.id)
            await onDeleted()
        } finally {
            setBusy(false)
            setConfirmDelete(false)
        }
    }

    const handleTestSend = async () => {
        if (!template || !testTo.includes('@')) return
        setTestResult('sending…')
        try {
            const r = await adminApi.testSendTemplate(template.id, testTo)
            setTestResult(`sent: ${r.sent} · failed: ${r.failed}`)
        } catch (e) {
            setTestResult(e instanceof Error ? e.message : 'failed')
        }
    }

    return (
        <div className="space-y-6">
            <button
                onClick={onCancel}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
                <ChevronLeft className="h-4 w-4" /> Back to templates
            </button>

            <SectionTitle
                title={isNew ? 'New template' : (template!.name ?? template!.key)}
                description={
                    isNew
                        ? 'Templates are versioned by key — saving a new key creates v1; saving the same key creates v2, v3, …'
                        : `Version ${template!.version} · last edited ${new Date(template!.updatedAt).toLocaleString()}`
                }
                actions={
                    <>
                        {!isNew && (
                            <PillButton
                                variant="danger"
                                onClick={() => setConfirmDelete(true)}
                            >
                                <Trash2 className="h-3.5 w-3.5" /> Delete
                            </PillButton>
                        )}
                        <PillButton
                            variant="primary"
                            onClick={handleSave}
                            disabled={busy || !key || !subject || !bodyHtml}
                        >
                            {busy ? 'Saving…' : isNew ? 'Create' : 'Save as new version'}
                        </PillButton>
                    </>
                }
            />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="space-y-4 lg:col-span-2">
                    {isNew && (
                        <Card>
                            <CardContent>
                                <Field
                                    label="Template key (immutable)"
                                    hint="Lowercase, dot-separated. This is what code passes when sending."
                                    required
                                >
                                    <Input
                                        value={key}
                                        onChange={e => setKey(e.target.value)}
                                        placeholder="e.g. notification.welcome_day1"
                                    />
                                </Field>
                            </CardContent>
                        </Card>
                    )}

                    <Card>
                        <CardContent>
                            <Field label="Subject line" required>
                                <Input
                                    value={subject}
                                    onChange={e => setSubject(e.target.value)}
                                    placeholder="Reconnect Slack on Libra"
                                />
                            </Field>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="!py-2">
                            <div className="flex items-center justify-between">
                                <CardTitle className="!text-sm">Body</CardTitle>
                                <div className="flex items-center gap-1">
                                    <ViewBtn
                                        active={view === 'editor'}
                                        onClick={() => setView('editor')}
                                    >
                                        <FileText className="h-3.5 w-3.5" />
                                        Editor
                                    </ViewBtn>
                                    <ViewBtn
                                        active={view === 'preview'}
                                        onClick={() => setView('preview')}
                                    >
                                        <Eye className="h-3.5 w-3.5" />
                                        Preview
                                    </ViewBtn>
                                    <ViewBtn
                                        active={view === 'html'}
                                        onClick={() => setView('html')}
                                    >
                                        <Code className="h-3.5 w-3.5" />
                                        HTML
                                    </ViewBtn>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="!p-0">
                            {view === 'editor' && (
                                <div className="p-4">
                                    <RichTextEditor
                                        value={bodyHtml}
                                        onChange={setBodyHtml}
                                        variables={
                                            template?.category === 'DIGEST_BLOCK'
                                                ? [...USER_VARIABLES, ...DIGEST_VARIABLES]
                                                : TEMPLATE_VARIABLES
                                        }
                                        placeholder="Write your email body here…"
                                        minHeight={360}
                                    />
                                </div>
                            )}
                            {view === 'preview' && (
                                <div className="bg-gray-50 p-4">
                                    <iframe
                                        srcDoc={
                                            bodyHtml ||
                                            "<p style='color:#9CA3AF;font-family:sans-serif;padding:24px'>No content yet</p>"
                                        }
                                        className="min-h-[400px] w-full rounded-md border border-gray-200 bg-white"
                                        title="Template preview"
                                        sandbox=""
                                    />
                                </div>
                            )}
                            {view === 'html' && (
                                <textarea
                                    value={bodyHtml}
                                    onChange={e => setBodyHtml(e.target.value)}
                                    placeholder="<div>...</div>"
                                    rows={20}
                                    className="w-full resize-y border-0 p-4 font-mono text-xs text-gray-900 outline-none"
                                />
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Field
                                label="Plain text fallback (optional)"
                                hint="Plain-text version for clients that don't render HTML."
                            >
                                <textarea
                                    value={bodyText ?? ''}
                                    onChange={e => setBodyText(e.target.value)}
                                    placeholder="Plain-text version…"
                                    rows={5}
                                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                            </Field>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="!text-sm">Status</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Toggle
                                checked={active}
                                onChange={setActive}
                                label="Active"
                                description="Inactive templates can't be sent through the engine."
                            />
                        </CardContent>
                    </Card>

                    {!isNew && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="!text-sm">Test send</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <p className="text-xs text-gray-500">
                                    Sends this template to one address. Subject is prefixed with{' '}
                                    <code className="font-mono">[TEST]</code>. Bypasses the
                                    suppression list.
                                </p>
                                <Input
                                    type="email"
                                    value={testTo}
                                    onChange={e => setTestTo(e.target.value)}
                                    placeholder="you@letslibra.com"
                                />
                                <PillButton
                                    variant="primary"
                                    onClick={handleTestSend}
                                    disabled={!testTo.includes('@')}
                                >
                                    <Send className="h-3.5 w-3.5" /> Send test
                                </PillButton>
                                {testResult && (
                                    <div className="rounded border border-gray-200 bg-gray-50 p-2 font-mono text-xs text-gray-700">
                                        {testResult}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>

            <ConfirmDialog
                open={confirmDelete}
                onCancel={() => setConfirmDelete(false)}
                onConfirm={handleDelete}
                title="Delete template?"
                message={
                    template
                        ? `This will permanently remove "${template.key}" v${template.version}. Other versions of this key (if any) are unaffected.`
                        : ''
                }
                confirmLabel="Delete"
                confirmVariant="danger"
                loading={busy}
            />
        </div>
    )
}

function ViewBtn({
    active,
    onClick,
    children,
}: {
    active: boolean
    onClick: () => void
    children: React.ReactNode
}) {
    return (
        <button
            onClick={onClick}
            className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                active
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
        >
            {children}
        </button>
    )
}
